<template>
  <div class="ss-root">
    <button
      type="button"
      class="ss-trigger"
      :class="{ open: open }"
      @click="toggle"
    >
      <span class="ss-value" :class="{ placeholder: !selectedLabel }">{{ selectedLabel || placeholder }}</span>
      <i class="material-icons ss-chevron">expand_more</i>
    </button>
    <div v-if="open" class="ss-panel">
      <div class="ss-search-wrap">
        <i class="material-icons ss-search-icon">search</i>
        <input
          ref="search"
          v-model="query"
          class="ss-search-input"
          :placeholder="searchPlaceholder"
          @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)"
          @keydown.enter.prevent="selectHighlighted"
          @keydown.esc="close"
        />
      </div>
      <ul class="ss-list">
        <li v-if="filteredOptions.length === 0" class="ss-empty">{{ emptyText }}</li>
        <li
          v-for="(opt, i) in filteredOptions"
          :key="opt.value"
          class="ss-option"
          :class="{ highlighted: i === highlightedIndex, selected: opt.value === value }"
          @mouseenter="highlightedIndex = i"
          @mousedown.prevent="select(opt)"
        >
          <i class="material-icons ss-check">{{ opt.value === value ? 'check' : '' }}</i>
          <span class="ss-option-label">{{ opt.label }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: "SearchableSelect",
  props: {
    value: {
      type: String,
      default: ''
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'Selecione'
    },
    searchPlaceholder: {
      type: String,
      default: 'Buscar...'
    },
    emptyText: {
      type: String,
      default: 'Nenhum resultado encontrado.'
    }
  },
  data() {
    return {
      open: false,
      query: '',
      highlightedIndex: 0
    }
  },
  computed: {
    selectedLabel() {
      const match = this.options.find(o => o.value === this.value)
      return match ? match.label : ''
    },
    filteredOptions() {
      if (!this.query) return this.options
      const q = this.query.toLowerCase()
      return this.options.filter(o => o.label.toLowerCase().includes(q))
    }
  },
  methods: {
    toggle() {
      this.open ? this.close() : this.openPanel()
    },
    openPanel() {
      this.open = true
      this.query = ''
      this.highlightedIndex = 0
      this.$nextTick(() => this.$refs.search && this.$refs.search.focus())
    },
    close() {
      this.open = false
    },
    select(opt) {
      this.$emit('input', opt.value)
      this.close()
    },
    selectHighlighted() {
      const opt = this.filteredOptions[this.highlightedIndex]
      if (opt) this.select(opt)
    },
    move(delta) {
      const max = this.filteredOptions.length - 1
      if (max < 0) return
      let next = this.highlightedIndex + delta
      if (next < 0) next = max
      if (next > max) next = 0
      this.highlightedIndex = next
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) this.close()
    }
  },
  watch: {
    query() {
      this.highlightedIndex = 0
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  }
}
</script>

<style scoped>
.ss-root {
  position: relative;
  width: 100%;
  font-size: 14px;
}

.ss-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.ss-trigger:hover {
  border-color: rgba(255, 255, 255, 0.35);
}

.ss-trigger.open {
  border-color: rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.08);
}

.ss-value.placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.ss-chevron {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.6);
}

.ss-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: #262626;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  z-index: 20;
  overflow: hidden;
}

.ss-search-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.ss-search-icon {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.5);
}

.ss-search-input {
  flex: 1;
  min-width: 0;
  background: transparent;
  border: none;
  outline: none;
  color: #fff;
  font-size: 16px;
  padding: 2px 0;
}

.ss-search-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.ss-list {
  list-style: none;
  margin: 0;
  padding: 4px;
  max-height: 220px;
  overflow-y: auto;
}

.ss-empty {
  padding: 12px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
}

.ss-option {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 11px 10px;
  border-radius: 6px;
  color: #fff;
  cursor: pointer;
}

.ss-option.highlighted {
  background: rgba(255, 255, 255, 0.1);
}

.ss-option.selected {
  color: #fff;
  font-weight: 500;
}

.ss-option-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ss-check {
  flex-shrink: 0;
  font-size: 16px;
  width: 16px;
  color: #fff;
}

@media only screen and (max-width: 480px) {
  .ss-trigger {
    padding: 9px 10px;
    font-size: 13px;
  }

  .ss-option {
    padding: 10px 8px;
    font-size: 13px;
  }

  .ss-list {
    max-height: 45vh;
  }
}
</style>
